import { useState, useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from 'helpers/api'
import Collapsible from 'components/common/Collapsible/Collapsible'
import Button from 'components/common/Button/Button'
import LogoShort from 'components/assets/icons/LogoShort'
import RoundArrow from 'components/assets/icons/RoundArrow'
import Title from 'components/common/PageTitle/PageTitle'
import { next } from 'components/Freemium/Nav/Nav'
import { dateToAge } from 'functions'
import { parseToken } from 'helpers/auth'
import Link from 'components/common/Link/Link'
import classNames from 'classnames'
import style from './More.module.scss'
import mainStyle from '../FreemiumComponents.module.scss'

const More = () => {
  const { state, pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [paidOption, setPaidOption] = useState(!!state?.client?.paidOption || !state.contractPay)
  const [contractTypes, setContractTypes] = useState(state?.contract?.contractType)
  const [calculatedPrice, setCalculatedPrice] = useState(0)
  const [loading, setLoading] = useState(true)
  const token = searchParams.get('token')
  const adhesionId = parseToken(token)?.adhesionId
  const partnerToken = searchParams.get('partner')
  const { setPartnerLogo } = useOutletContext()


  useEffect(() => {

    const fetchPrice = async (name, age) => {
      try{
        return age >= 18
          ? Number((await api.get({ url: `/freemium/${name}/${age}` })).result)
          : 0
      } catch (error) {
        throw new Error('Failed to fetch price')
      }
    }

    const getPrices = async () => {
      const userAge = dateToAge(state.client.birthdate)
      const userPrice = await fetchPrice('age', userAge)
      const partnerAge = dateToAge(state.client.partnerOption ? state.client.birthdatePartner : 0)
      const partnerPrice = await fetchPrice('agePartner', partnerAge)
      setCalculatedPrice(parseFloat(userPrice + partnerPrice).toFixed(2).replace('.', ','))
    }

    const getContract = async () => {
      try {
        const contracts = state?.contract?.contractType ?? []
        const contractType = await api.get({
          url: `/owen-products/contract_type/${state.contractPay}/client`
        })
        const wordings = await api.get({
          url: `/contract-type/${state.contractPay}/wordings`,
          headers: { Authorization: `Bearer ${state?.token || partnerToken}` }
        })
        if (!contracts.find( contract => contract._id === state.contractPay )){
          contracts.push({...contractType, wordings})
        }
        setPartnerLogo(wordings?.logo)
        setContractTypes(contracts)
        setLoading(false)
      } catch (error) {
        console.log('Fetch contracts error', error.message)
      }
    }
    if (state.contractPay){
      getContract()
    } else {
      setLoading(false)
    }
    getPrices()
  }, [state, adhesionId, token, partnerToken, searchParams, navigate, setPartnerLogo])


  // Navigate to the next page
  const submit = () => {
    next({
      pathname, navigate, state,
      data: {
        client: { paidOption },
        contract: {
          contractType: paidOption ? contractTypes : [contractTypes[0]],
          calculatedPrice
        }
      },
      skip: paidOption ? 0 : 1
    })
  }

  if (loading || calculatedPrice === 0 || (state.contractPay && contractTypes.length !== 2)) return
  const {
    guarantees,
    titleGuarantees,
    subTitlePromotion,
    labelButtonActivation,
    coveredEvents,
    indemnities,
    description,
    priceValue,
    promotionalHook,
    legalTerms
  } = contractTypes.slice(-1)[0].wordings?.more || {}
  const {
    titleActivationGuarantees,
    descriptionActivationGuarantees
  } = contractTypes[0].wordings?.more || {}
  const miumOnly = contractTypes.length === 1

  const date = new Date()
  date.setMonth( date.getMonth() + 1 )
  const startDate = `01/${('0'+Number(date.getMonth() + 1)).slice(-2)}/${date.getFullYear()}`


  return (
    <>
      <Title
        className={mainStyle['page-title']}
        text={
          (miumOnly
            ? t('freemium:more.titleMiumOnly')
            : t('freemium:more.title')
          )+' 🛡️'
        }
      />

      <div className={style.info}>
        <span>🎉 {titleActivationGuarantees || 'titleActivationGuarantees'}</span>
        {!miumOnly && <span>{t('freemium:more.startDate', { startDate })}</span>}
        <p dangerouslySetInnerHTML={{__html:(descriptionActivationGuarantees?.replace(/{{(.+)}}/g, state?.client.partnerOption ? "$1" : "") || 'descriptionActivationGuarantees')+' ⬇️'}}></p>
        <b>{promotionalHook || 'promotionalHook'}</b>
      </div>

      <div className={style.info}>
        <ul>
          <li><span><RoundArrow /></span>{t('freemium:more.catchLine1')}</li>
          <li><span><RoundArrow /></span>{t('freemium:more.catchLine2')}</li>
        </ul>
        <p className={style.center}>{t('freemium:more.solution')}</p>
      </div>


      <div className={style.cards}>

        <div className={style.main}>
          <span>
            {subTitlePromotion}
          </span>

          <h3 className={classNames(mainStyle.shining_title, mainStyle.max)}>
            {titleGuarantees}
          </h3>

          <ul>
            {(guarantees || '').split('//').map((item, index) => (
              <li key={`covered-event-${index}`}>
                {item}
              </li>
            ))}
          </ul>

          <div style={{textAlign: miumOnly && 'center'}}>
            {labelButtonActivation}
            <span className={style.price}>{calculatedPrice}{priceValue}</span>

            {!miumOnly && <label className={style.toggle}>
              <input
                type='checkbox'
                checked={paidOption}
                onChange={() => setPaidOption(!paidOption)}
                hidden
              />
              <span></span>
            </label>}
          </div>
        </div>

        <div className={style.product}>
          <h4 className={style['product-title']}>
            <LogoShort className={style.logo} /> {t('common:yourProtection')}
          </h4>
          <p className={style['product-description']}>
            {description}
          </p>
        </div>

        <Collapsible
          className={style['collapsible-card']}
          title={<span className={style['list-title']}>🛡 {t('freemium:start.eventsCovered')}</span>}
          isOpenedProp={true}
          content={
            <ul className={style.list}>
              {(coveredEvents || '').split('//').map((item, index) => (
                <li className={style.item} key={`covered-event2-${index}`}>
                  {item}
                </li>
              ))}
            </ul>
          }
        />

        <Collapsible
          className={style['collapsible-card']}
          title={<span className={style['list-title']}>💸 {t('freemium:start.indemnity')}</span>}
          isOpenedProp={true}
          content={
            <ul className={style.list}>
              {(indemnities || '').split('//').map((item, index) => (
                <li className={style.item} key={`indemnity-${index}`}>
                  {item}
                </li>
              ))}
            </ul>
          }
        />

        <Button
          className={classNames(mainStyle.button, style.button)}
          label={paidOption
            ? t('freemium:more.confirmProtection')
            : t('freemium:more.rejectProtection')
          }
          onClick={submit}
          type="primary"
        />

        <p className={style.link}>
          {`${t('common:consultThe')} `}
          <Link
            label={t('contract:document.ipid').toLowerCase()}
            target="_blank"
            to={state?.contract?.contractType.slice(-1)[0].legalDocuments.ni}
          />
          {` ${t('common:misc.andThe')} `}
          <Link
            label={t('contract:document.niShort').toLowerCase()}
            target="_blank"
            to={state?.contract?.contractType.slice(-1)[0].legalDocuments.ipid}
          />
          {` ${t('common:misc.onThe')} ${t('common:insuranceProduct')}`}
        </p>

        <span className={style.legalterms}>{legalTerms}</span>
      </div>
    </>
  )
}

export default More